import { Suspense, lazy, useEffect, useState } from 'react';
import { runInit, type InitStep } from '@mononoke/shared';
import { detectBootKind, ledger, waterfall } from './platform/browser.js';
import { subscribeUpdatePending } from './update/pending.js';
import { UpdatePrompt } from './update/update-prompt.js';
import { authConfigured, initAuth, signOut, subscribeAuth, type AuthState } from './platform/auth.js';
import { api, call } from './platform/api.js';
import { SignIn } from './screens/signin.js';
// ⚠️ NOT LAZY, unlike the boxes screens. It is the first thing an invited person ever loads,
// often on a phone, and a code-split chunk fetched at that moment is one more thing that can
// fail between an invitation and a seat. It is also small — a card and two fields.
import { Join } from './screens/join.js';
import { AppNav } from './components/nav.js';
import { Items } from './screens/items.js';

/**
 * The boxes screens are LAZY — the one code-split point in the app, and it exists for the
 * bundle budget, not for elegance: 0.1.164's screens pushed the entry bundle 6.8 kB over
 * the 500 kB ceiling in CI (locally Sentry is excluded and the smoke passed — the ceiling
 * is measured on the SHIPPED composition). The budget note says do not raise it, so the
 * newest screen pays the toll. One chunk for all three; the fallback matches the screens'
 * own loading line so the seam is invisible.
 */
const Boxes = lazy(() => import('./screens/boxes.js').then((m) => ({ default: m.Boxes })));
const BoxAdd = lazy(() => import('./screens/boxes.js').then((m) => ({ default: m.BoxAdd })));
// ⚠️ LAZY, like every other screen here. The bundle budget is the reason the app has any
// lazy chunks at all (0.1.174 went over it), and a whole new screen loaded eagerly is
// exactly the kind of addition that quietly spends the headroom.
const RetourScreen = lazy(() => import('./screens/retour.js').then((m) => ({ default: m.RetourScreen })));
// BOOKINGS WENT LAZY at flow tabs P2 (0.1.463): the list screens carry the display kit now, and
// loaded eagerly they pushed the boot path over its 500 kB ceiling (CI's smoke read 501.4 kB —
// the local build without the Sentry DSN reads lower, which is why it was not caught first).
// The detail rides the same module, so it is the same chunk, loaded once.
const BookingsScreen = lazy(() => import('./screens/bookings.js').then((m) => ({ default: m.BookingsScreen })));
const BookingScreen = lazy(() => import('./screens/bookings.js').then((m) => ({ default: m.BookingScreen })));
// LAZY, like the boxes/retour screens — a whole new screen (and it dynamically pulls the QR
// encoder when placards print) has no business on the boot path. The bundle budget is the reason.
const RackLocationScreen = lazy(() =>
  import('./screens/rack-location.js').then((m) => ({ default: m.RackLocationScreen })),
);
const BoxDetailScreen = lazy(() => import('./screens/boxes.js').then((m) => ({ default: m.BoxDetailScreen })));
const OutfitDetailScreen = lazy(() => import('./screens/outfit-detail.js').then((m) => ({ default: m.OutfitDetailScreen })));
// Projects (063) — LAZY like every other screen, for the bundle budget: three new screens have
// no business on the boot path. Each suspends on navigation, so each MUST have a Suspense wrapper.
const ProjectsScreen = lazy(() => import('./screens/projects.js').then((m) => ({ default: m.ProjectsScreen })));
const ProjectDetailScreen = lazy(() => import('./screens/project-detail.js').then((m) => ({ default: m.ProjectDetailScreen })));
const CharacterDetailScreen = lazy(() => import('./screens/character-detail.js').then((m) => ({ default: m.CharacterDetailScreen })));
import { DashboardScreen } from './screens/dashboard.js';
// LAZY (0.1.222): item detail is the second-largest screen and it grew with the redesign
// (icon pills, More menu, the photo strip, the inline edit). Loading it eagerly put all of
// that on the boot path, which CI's 500 kB ceiling refused — the same budget that already
// splits Boxes and Retour. It suspends on navigation, so it MUST have a Suspense wrapper.
const ItemDetailScreen = lazy(() => import('./screens/item-detail.js').then((m) => ({ default: m.ItemDetailScreen })));
const ItemAdd = lazy(() => import('./screens/item-add.js').then((m) => ({ default: m.ItemAdd })));
import { SettingsScreen } from './screens/settings.js';
import { BookingCheckinScreen } from './screens/booking-checkin.js';
import { MaintenanceScreen } from './screens/maintenance.js';
// LAZY since 0.1.432 (display-options P1): the outfits screen was the last big list still
// on the boot path, and it now carries the desk datasheet engine (src/display/). The
// bundle budget is the reason, same as every lazy screen above. It suspends on
// navigation, so it MUST have a Suspense wrapper.
const OutfitsScreen = lazy(() => import('./screens/outfits.js').then((m) => ({ default: m.OutfitsScreen })));
import { ScanScreen } from './screens/scan.js';
import { subscribeRoute, type Route } from './platform/router.js';
import { prefetchItem, preloadItemDetailChunk } from './platform/item-prefetch.js';
import { forgetIntakeMeta } from './platform/intake-meta.js';
import { QueueBadge } from './components/queue-badge.js';
import { ErrorBoundary } from './components/error-boundary.js';

/**
 * Boot steps. Every variant (cold / resume / auth-redirect) runs through this one
 * list — see runInit's header comment for why that matters.
 */
const steps: InitStep[] = [
  {
    name: 'session',
    skipOn: [],
    run: async () => {
      // Placeholder until auth lands (Phase 0 step 7). Present now so the ordering
      // and failure paths exist before the feature does.
      await Promise.resolve();
      waterfall.stamp('auth-ready');
    },
  },
  {
    name: 'catalog',
    needs: ['session'],
    run: async () => {
      await Promise.resolve();
      waterfall.stamp('data-ready');
    },
  },
];

/**
 * IS THIS ROUTE A PICKING LIST? (0.1.395)
 *
 * The three tabs that double as pickers (items, boxes, outfits) are the REAL list in a mode —
 * that is the point, so the picker cannot drift from the thing it picks from. But a mode with its
 * own back chip is a detail as far as the phone chrome is concerned, and leaving the tab bar up
 * offered an exit that silently abandoned the pick.
 *
 * Keyed on the picking params, never on the route name: `/items` must stay an ordinary tab.
 */
function isPicking(route: Route): boolean {
  if (route.name === 'items') {
    return (
      route.forOutfit !== undefined ||
      route.forBooking !== undefined ||
      route.forCharacter !== undefined ||
      route.forProject !== undefined ||
      route.forBarcode !== undefined
    );
  }
  if (route.name === 'outfits') {
    return route.forCharacter !== undefined || route.forProject !== undefined || route.forBooking !== undefined;
  }
  if (route.name === 'reservations') return route.forProject !== undefined;
  if (route.name === 'boxes') {
    return route.forCharacter !== undefined || route.forProject !== undefined;
  }
  return false;
}

export function App(): JSX.Element {
  const [message, setMessage] = useState<string | null>(null);
  const [updateWaiting, setUpdateWaiting] = useState(false);
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' });
  const [route, setRoute] = useState<Route>({ name: 'dashboard' });
  /**
   * ⚠️ AN ACCOUNT IS NOT A SEAT, AND UNTIL 0.1.213 THE SHELL ASSUMED IT WAS.
   *
   * Reported from use, 9 Aug 2026: the maintainer created the invited account, confirmed the
   * address, and **signed in** — instead of returning to the invitation link and pressing Join.
   * Supabase authenticated them, `auth.status` became `signed-in`, and the shell rendered the
   * whole app for somebody with no row in `users`. Every query then failed `unknown_user`, so
   * they got a nav bar over a screen of errors, with nothing anywhere saying *"you have not
   * finished joining"*. Guardrail 10: that is a failure with no signal.
   *
   * This is the one state Guardrail 6 makes ordinary — *"creating an auth account is permitted
   * and grants nothing"* — so the app has to have a screen for it rather than treat it as
   * impossible.
   */
  /**
   * ⚠️ `checking` RENDERS THE APP, IT DOES NOT WITHHOLD IT. The first version of this gated the
   * whole shell on the `whoami` round-trip and showed *"Checking your access…"* until it
   * returned — which made **every signed-in page load** wait for a network call, and flashed a
   * navless shell on the way in. `sticky-layout.spec` caught it on the phone viewport.
   *
   * That is the wrong trade by a distance: the seatless state is rare and recoverable, and
   * paying for it on every boot by every seated user is how a fix for one person's dead end
   * becomes everyone's slow app. So the optimistic answer is the default, and the no-access
   * screen replaces it a moment later on a CLEAN REFUSAL only.
   */
  const [access, setAccess] = useState<'checking' | 'ok' | 'none'>('checking');
  const seated = access !== 'none';
  /** The whoami role, for HIDING nav areas a role cannot work in — never for gating.
   *  `null` until the read lands; the nav shows everything for null (its own comment says why). */
  const [role, setRole] = useState<string | null>(null);

  useEffect(() => subscribeRoute(setRoute), []);
  // THE ITEM LOADS AT THE TAP (0.1.526, platform/item-prefetch.ts): an item route starts its
  // `getItem` read as the route changes, in parallel with the lazy chunk and the first render,
  // instead of after them. Only while signed in — a read without a session is a 401 for nothing.
  useEffect(
    () =>
      subscribeRoute((r) => {
        if (r.name === 'item' && auth.status === 'signed-in') prefetchItem(r.id);
      }),
    [auth.status],
  );
  // …and the item-detail chunk itself is fetched once the signed-in shell is idle, so a first open
  // does not stop on the lazy screen's Suspense fallback ("an empty screen … then it flashes").
  useEffect(() => {
    // Signed out: the in-memory intake vocabulary goes with the session (platform/intake-meta.ts).
    if (auth.status !== 'signed-in') {
      forgetIntakeMeta();
      return;
    }
    const handle = window.setTimeout(preloadItemDetailChunk, 1_500);
    return () => window.clearTimeout(handle);
  }, [auth.status]);

  // Guardrail 10's visible half for a pending update. Rendered from state, never
  // appended to the DOM from the service-worker callback.
  useEffect(() => subscribeUpdatePending(setUpdateWaiting), []);
  useEffect(() => {
    const unsubscribe = subscribeAuth(setAuth);
    void initAuth();
    return unsubscribe;
  }, []);

  /**
   * Ask the database whether this account has a seat. One query, once per sign-in.
   *
   * ⚠️ ONLY `unauthenticated` MEANS "NO SEAT". A network failure or a cold API must NOT put
   * somebody on the no-access screen — that would tell a perfectly seated person they had been
   * removed, which is a worse lie than the one being fixed. Anything that is not a clean
   * refusal falls through to `ok` and lets the screens report their own trouble, which they
   * already do properly.
   */
  useEffect(() => {
    if (auth.status !== 'signed-in') {
      setAccess('checking');
      return;
    }
    let live = true;
    void (async () => {
      const result = await call('whoami', () => api.whoami.query());
      if (!live) return;
      setAccess(result.ok || result.failure.kind !== 'unauthenticated' ? 'ok' : 'none');
      setRole(result.ok ? result.data.role : null);
    })();
    return () => {
      live = false;
    };
  }, [auth.status]);

  useEffect(() => {
    waterfall.stamp('first-paint');
    void runInit(
      steps,
      {
        kind: detectBootKind(),
        onFailure: (error, step) => {
          const entry = ledger.record(error, `init:${step}`, { notified: true });
          setMessage(entry.userMessage);
        },
      },
      { required: ['session'] },
    );
  }, []);

  /**
   * ⚠️ `/join` SHORT-CIRCUITS THE WHOLE SHELL, and it is the only route that does since the
   * client view was deleted — `docs/self-service-accounts-v1_0.md` stage 2.
   *
   * Placed after every hook so the hook order is unchanged, and BEFORE the nav, the update
   * banner and the auth gate, because the person reading it has no seat and possibly no
   * account. The auth gate in particular would put a sign-in form in front of somebody whose
   * whole problem is that they have not made an account yet.
   *
   * It is handed `auth` rather than reading it, because the screen genuinely renders three
   * different things depending on it — create an account, sign in, or accept.
   */
  if (route.name === 'join') {
    return (
      <div className="app">
        <main className="app-main">
          <ErrorBoundary where="screen:join" what="This screen">
            <Join token={route.token} auth={auth} />
          </ErrorBoundary>
        </main>
      </div>
    );
  }

  /**
   * THE CLIENT VIEW SHORT-CIRCUITS THE WHOLE SHELL — client-delivery-plan §5.
   *
   * Placed after every hook, so the hook order is unchanged, and before the header, nav,
   * update banner and auth gate, because a client must see none of them. They are not a
   * user of this application: showing them "Mononoke v0.1.x", a Diagnostics button and a
   * sign-in form would be confusing at best and an invitation to try the form at worst.
   *
   * It renders whatever `auth.status` says, including `signed-out`, which is the normal
   * case here and the only screen in the app for which that is true.
   */
  return (
    // The signed-in class is what the desktop grid hangs off — without it the CSS
    // has no sidebar column and the nav lands IN FLOW, shoving content below the
    // fold and starving raw-pointer gestures (drag-select, the signature pad) of
    // their coordinates. Found by elementFromPoint probing, 29 Jul.
    <div
      className={[
        'app',
        auth.status === 'signed-in' && seated ? 'app-signed-in' : '',
        // A DETAIL SCREEN DROPS THE PHONE TAB BAR (redesign 1e/14c/11b/15a): the frames
        // give a detail its own back chip and bottom act bar instead — CSS hides the nav
        // under 700px for these routes only; the desk sidebar is untouched.
        // ⚠️ A PICKING LIST COUNTS AS A DETAIL TOO (0.1.395). The picker modes are the real
        // tab in a mode, so the tab bar stayed on screen while picking — offering a way out
        // that abandons the pick with no sign it was abandoned, and a way BACK IN that lands
        // on the ordinary tab. The mode has its own back chip, exactly like a detail does, so
        // it takes the same treatment. Keyed on the picking params only: the plain tabs are
        // untouched.
        route.name === 'item' || route.name === 'box' || route.name === 'outfit' || route.name === 'booking' || route.name === 'booking-checkin' || route.name === 'boxAdd' || route.name === 'project' || route.name === 'character' || isPicking(route)
          ? 'app-detail'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/**
        * THE HEADER BAND IS GONE (maintainer, 6 Aug 2026: "remove the header band of the app
        * name and version number, we have this information in the general settings tab").
        * Settings → About carries the same line, and `/readyz` answers it from outside the
        * app. ⚠️ boot.spec and offline-shell.spec used to read the version chip HERE; they
        * read the Settings About line now — the assertion moved, it did not die, because
        * "the shell knows its own version" is the update-path check the release ritual
        * depends on. The diagnostics-button note that lived here moved with its button in
        * 0.1.170; nothing else did.
        */}
      {/* Gated on the SEAT, not just the session: a nav bar over the no-access card would
          offer tabs that cannot load, which is the broken-app impression this release fixes. */}
      {auth.status === 'signed-in' && seated && <AppNav route={route} role={role} />}
      {/* Above the routed screen, not inside one: a queued photo outlives the screen
          that captured it, so the honest place for the count is the shell. */}
      {auth.status === 'signed-in' && seated && <QueueBadge />}
      {/* DATASHEET ROUTES GO FULL BLEED at desk widths (display-options P1 feedback,
          1 Sep 2026): .app-main-ds strips the raised pane so the sheet runs edge-to-edge
          on --bg, exactly as the frames draw it. Boxes and items join this list when they
          adopt the engine (P3/P5); the class is inert at phone widths. */}
      <main
        className={
          route.name === 'outfits' ||
          // The outfit DETAIL joined at P6 (frame 3): full-bleed two-column body with the
          // comments rail. Box and item details keep the raised pane — not rebuilt.
          route.name === 'outfit' ||
          route.name === 'boxes' ||
          // Projects' desk half joined with the flow-tabs build (frame 13c); its phone list
          // was already on the engine (0.1.448).
          route.name === 'projects' ||
          // Check-outs joined at flow tabs P2 (frame 13b); Reservations at P3 (13a).
          route.name === 'checkouts' ||
          route.name === 'reservations' ||
          // Retour joined at flow tabs P4 (frame 13d); the Runs half rides above the sheet in an inset.
          route.name === 'retour' ||
          // Maintenance joined at 0.1.491: the queue is the Items sheet's design, full bleed.
          route.name === 'maintenance' ||
          // The dashboard joined at 0.1.493 (frame 16b, A3): the two count lists share the top band,
          // the activity ledger takes the full width beneath — full bleed, the sheet scrolls.
          route.name === 'dashboard' ||
          // Items joins at P5 — the UNSCOPED catalogue, the reservation's picker since 0.1.523, and
          // EVERY picker since 0.1.528 (maintainer, 3 Oct 2026: "all the picker screens should also
          // have their display function … the same as their original tab list"): each draws the
          // tab's own table at a desk now, so each runs full bleed like it. Inert on a phone.
          route.name === 'items'
            ? 'app-main app-main-ds'
            : 'app-main'
        }
      >
        {message !== null && (
          <p role="alert" className="banner banner-alert">
            {message}
          </p>
        )}
        {/* The fresh-update Confirm (S2-c) — hosts its own dialog, renders nothing at rest. */}
        <UpdatePrompt />
        {updateWaiting && (
          <p role="status" className="banner">
            A new version is ready. Close and reopen the app to apply it.
          </p>
        )}
        {!authConfigured() && (
          <p role="alert" className="banner banner-alert">
            This build has no Supabase configuration, so sign-in is unavailable.
          </p>
        )}
        {authConfigured() && auth.status === 'loading' && <p role="status">Checking your session…</p>}
        {authConfigured() && auth.status === 'signed-out' && <SignIn />}
                {/**
          * SIGNED IN, NO SEAT — the state Guardrail 6 makes ordinary and the shell had no screen
          * for. It says the true thing (the account exists, the access does not), and gives the
          * two actions that can resolve it. It does NOT offer a Join button: this screen has no
          * token, and the invitation link is the only thing that carries one.
          */}
        {auth.status === 'signed-in' && access === 'none' && (
          <section className="auth-card">
            <h2>This account has no access yet</h2>
            <p>
              You are signed in as <strong>{auth.email ?? 'your account'}</strong>, but this account is not part of a
              workspace.
            </p>
            {/* The one loud thing on the card (frame 11a, rule 6): the step people lose, as a
                warn band rather than a grey aside — signing in is not the last act. */}
            <p className="auth-callout">
              If you were invited, <strong>open the invitation link from your email and press Join</strong> — signing in
              is not the last step.
            </p>
            {/* The same 401 also covers an account whose access was TURNED OFF — the API
                deliberately does not tell a signed-out-for-good person which it is, and neither
                do we. One hedged sentence covers the second case without becoming an oracle. */}
            <p>
              <small>
                If you have been waiting a while, ask whoever invited you to send it again. If you used to have access
                here, an administrator has switched it off.
              </small>
            </p>
            <button type="button" className="auth-secondary" onClick={() => void signOut()}>
              Sign out
            </button>
          </section>
        )}
        {auth.status === 'signed-in' && seated && (
          /**
           * ONE BOUNDARY PER SCREEN, INSIDE THE NAV — 0.1.89, after a render throw on the
           * item screen made a flagged garment permanently unopenable (0.1.88).
           *
           * Placed HERE rather than around the whole app on purpose: the nav bar renders
           * outside it, so a screen that dies leaves you able to walk away from it. A single
           * boundary at the root would have replaced the entire application with an apology,
           * which is the same dead end in nicer wording.
           *
           * KEYED ON THE ROUTE, and that is the part that is easy to leave out. A boundary
           * holds its error until something resets it — without the key, navigating away
           * from a broken screen would carry the fallback to the next one, and the app would
           * look broken everywhere. Changing the key remounts it, so leaving is the reset.
           */
          <ErrorBoundary key={route.name} where={`screen:${route.name}`} what="This screen">
            {route.name === 'dashboard' && <DashboardScreen />}
            {/* Keyed on the status filter so a dashboard card always lands on a
                freshly-filtered list, never a stale one from a previous visit. */}
            {route.name === 'items' && (
              <Items
                key={
                  // A different scanned code is a different errand: remount, never re-use the pick.
                  (route.forBarcode !== undefined ? `code:${route.forBarcode}` : undefined) ??
                  route.forOutfit ??
                  route.forBooking ??
                  // The character picker keys on BOTH: the same production picked for two
                  // characters must remount, or the second pick files against the first.
                  (route.forCharacter !== undefined ? `char:${route.forCharacter}` : undefined) ??
                  (route.forProject !== undefined ? `proj:${route.forProject}` : undefined) ??
                  route.status?.join(',') ??
                  'all'
                }
                initialStatus={route.status}
                forOutfit={route.forOutfit}
                forBooking={route.forBooking}
                forCharacter={route.forCharacter}
                forProject={route.forProject}
                forBarcode={route.forBarcode}
              />
            )}
            {route.name === 'item' && (
              <Suspense fallback={<p>Loading…</p>}>
                <ItemDetailScreen id={route.id} from={route.from} role={role} />
              </Suspense>
            )}
            {/* Rule 1 (A4, frame 2c): /add mints an empty item and replace-navigates to its
                detail. `boxId` files it into a box being packed; `outfitId` (frame 5c "Add a new
                item") files it straight into a look. */}
            {route.name === 'add' && (
              <Suspense fallback={<p>Loading…</p>}>
                <ItemAdd boxId={route.boxId} outfitId={route.outfitId} barcode={route.barcode} />
              </Suspense>
            )}
            {/* ⚠️ SUSPENSE IS NOT OPTIONAL AROUND A `lazy()` SCREEN, and this shipped without
                it in 0.1.187. Navigation is synchronous input; a component that suspends
                during it makes React throw #426 — "A component suspended while responding to
                synchronous input" — and the error boundary catches it as a dead screen.

                Every other lazy screen below has this wrapper. The one that did not was the
                one added last, which is the shape this comment exists to break. */}
            {route.name === 'retour' && (
              <Suspense fallback={<p>Loading…</p>}>
                <RetourScreen />
              </Suspense>
            )}
            {route.name === 'boxes' && (
              <Suspense fallback={<p>Loading…</p>}>
                <Boxes
                  key={route.forCharacter ?? route.forProject ?? 'board'}
                  floor={route.floor ?? null}
                  forCharacter={route.forCharacter}
                  forProject={route.forProject}
                />
              </Suspense>
            )}
            {route.name === 'racks' && (
              <Suspense fallback={<p>Loading…</p>}>
                <RackLocationScreen floor={route.floor ?? null} role={role} />
              </Suspense>
            )}
            {route.name === 'boxAdd' && (
              <Suspense fallback={<p>Loading…</p>}>
                <BoxAdd />
              </Suspense>
            )}
            {route.name === 'box' && (
              <Suspense fallback={<p>Loading…</p>}>
                <BoxDetailScreen id={route.id} from={route.from} />
              </Suspense>
            )}
            {route.name === 'outfit' && (
              <Suspense fallback={<p>Loading…</p>}>
                <OutfitDetailScreen id={route.id} from={route.from} />
              </Suspense>
            )}
            {route.name === 'projects' && (
              <Suspense fallback={<p>Loading…</p>}>
                <ProjectsScreen />
              </Suspense>
            )}
            {route.name === 'project' && (
              <Suspense fallback={<p>Loading…</p>}>
                <ProjectDetailScreen id={route.id} />
              </Suspense>
            )}
            {route.name === 'character' && (
              <Suspense fallback={<p>Loading…</p>}>
                <CharacterDetailScreen projectId={route.projectId} id={route.id} />
              </Suspense>
            )}
            {route.name === 'settings' && <SettingsScreen role={role} />}
            {/* `/users` and `/import` are PANELS of Settings since 0.1.172. The routes still
                resolve so links and bookmarks land where they used to — they just open the
                settings screen on the right tab.

                `/deadlines` is NOT here: 0.1.171 removed that screen entirely, and this branch
                merged that removal rather than reinstating a route to a deleted component. */}
            {route.name === 'users' && <SettingsScreen panel="users" role={role} />}
            {route.name === 'reservations' && (
              <Suspense fallback={<p>Loading…</p>}>
                <BookingsScreen
                  key={route.forProject === undefined ? 'list' : `pick:${route.forProject}:${route.forCharacter ?? ''}`}
                  variant="reservations"
                  forProject={route.forProject}
                  forCharacter={route.forCharacter}
                />
              </Suspense>
            )}
            {route.name === 'checkouts' && (
              <Suspense fallback={<p>Loading…</p>}>
                <BookingsScreen variant="checkouts" />
              </Suspense>
            )}
            {route.name === 'booking' && (
              <Suspense fallback={<p>Loading…</p>}>
                <BookingScreen id={route.id} from={route.from} origin={route.origin} />
              </Suspense>
            )}
            {route.name === 'booking-checkin' && <BookingCheckinScreen id={route.id} />}
            {route.name === 'maintenance' && <MaintenanceScreen />}
            {route.name === 'outfits' && (
              <Suspense fallback={<p>Loading…</p>}>
                <OutfitsScreen
                  forCharacter={route.forCharacter}
                  forProject={route.forProject}
                  slot={route.slot}
                  forBooking={route.forBooking}
                />
              </Suspense>
            )}
            {route.name === 'scan' && (
              // Keyed on the link that brought us here, so "Linked to X" after a pick is a fresh
              // screen rather than the previous scan's leftover state.
              <ScanScreen
                key={route.linked === undefined ? 'scan' : `linked:${route.linked.itemId}:${route.linked.code}`}
                linked={route.linked}
                role={role}
              />
            )}
            {route.name === 'import' && <SettingsScreen panel="import" role={role} />}
          </ErrorBoundary>
        )}
      </main>
    </div>
  );
}
